'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getShowPromoBarAction } from '@/features/subscriptions/actions';
import { Button } from '@/shared';
import { XMarkIcon } from '@/shared/ui/icon';

import {
	PROMO_BAR_STORAGE_KEY,
	PROMO_BAR_URL,
	promoBarData,
} from '../constants';
import { isPromoBarExpired, shouldShowPromoBarOnPage } from '../utils';

export const PromoBar = () => {
	const pathname = usePathname();
	const [hiddenDom, setHiddenDom] = useState(true);
	const [isVisible, setIsVisible] = useState(false);
	const [isMounted, setIsMounted] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let timerId: ReturnType<typeof setTimeout>;
		(async () => {
			const showPromoBar = await getShowPromoBarAction();
			if (!isPromoBarExpired() && showPromoBar) {
				setHiddenDom(false);
				timerId = setTimeout(() => {
					setIsVisible(true);
				}, 100);
			}
			setIsMounted(true);
		})();
		return () => {
			if (timerId) {
				clearTimeout(timerId);
			}
		};
	}, []);

	// Проверяем, нужно ли показывать промо-бар на текущей странице
	const shouldShowPromoBarInThisPage = useMemo(
		() => shouldShowPromoBarOnPage(pathname),
		[pathname],
	);

	// Управляем CSS переменной --topbar-height в зависимости от реальной высоты промобара
	// для нормальной работы стики блокков
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const updateTopbarHeight = () => {
			if (isVisible && shouldShowPromoBarInThisPage) {
				const height = container.offsetHeight;
				document.documentElement.style.setProperty(
					'--topbar-height',
					`${height}px`,
				);
			} else {
				document.documentElement.style.setProperty('--topbar-height', '0px');
			}
		};

		updateTopbarHeight();

		const resizeObserver = new ResizeObserver(() => {
			updateTopbarHeight();
		});

		resizeObserver.observe(container);

		return () => {
			document.documentElement.style.setProperty('--topbar-height', '0px');
			resizeObserver.disconnect();
		};
	}, [isVisible, shouldShowPromoBarInThisPage]);

	const handleClose = () => {
		setIsVisible(false);
		setHiddenDom(true);
		localStorage.setItem(PROMO_BAR_STORAGE_KEY, Date.now().toString());
	};

	const isCommunityPage = pathname === '/community';

	const buttonComputedProps = useMemo(
		() =>
			isCommunityPage
				? {
						onClick: () => {
							const priceAnchor = document.getElementById('price');
							if (priceAnchor) {
								priceAnchor.scrollIntoView({ behavior: 'smooth' });
							}
						},
					}
				: {
						href: PROMO_BAR_URL,
					},
		[isCommunityPage],
	);

	const shouldRender = isMounted && isVisible && shouldShowPromoBarInThisPage;

	if (hiddenDom || !shouldShowPromoBarInThisPage) {
		return null;
	}

	return (
		<>
			<div
				ref={containerRef}
				className="overflow-hidden transition-all delay-500 duration-1000 ease-out"
				style={{
					backgroundColor: promoBarData.bgColor,
					transform: shouldRender ? 'translateY(0)' : 'translateY(-100%)',
				}}
			>
				<div className="relative mx-auto flex items-center justify-center px-4 py-1 md:py-2">
					{/* Кнопка закрытия */}
					<button
						type="button"
						onClick={handleClose}
						className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1 transition-colors"
						aria-label="Закрыть промо-бар"
					>
						<XMarkIcon
							className="size-4"
							style={{ color: promoBarData.textColor }}
						/>
					</button>

					{/* Контент */}
					<div className="flex flex-col items-center gap-2 px-8 xl:flex-row xl:gap-4">
						<h2
							className="text-center text-lg font-bold sm:text-left"
							style={{ color: promoBarData.textColor }}
						>
							{promoBarData.title}
						</h2>
						<p
							className="text-center sm:text-left"
							style={{ color: promoBarData.textColor }}
						>
							{promoBarData.description}
						</p>
						<Button
							{...buttonComputedProps}
							size="36"
							variant="red"
							className="whitespace-nowrap text-sm font-semibold"
						>
							{promoBarData.ctaLabel}
						</Button>
					</div>
				</div>
			</div>
		</>
	);
};
